import { css } from '@emotion/react';
import { IconButton, Stack, styled, Tooltip, Typography } from '@mui/joy';
import { flexRender, Header, RowData } from '@tanstack/react-table';

import AngleUpIcon from '@/assets/icons/angle-up.svg?react';
import FilterIcon from '@/assets/icons/filter.svg?react';
import { theme as globalTheme } from '@/setup/theme';
import { transientOptions } from '@/utils/transient-options';

const StyledIconButton = styled(
  IconButton,
  transientOptions,
)<{ $active: boolean }>(
  ({ theme, $active }) => css`
    border-radius: 50%;
    min-width: ${theme.spacing(3)};
    min-height: ${theme.spacing(3)};
    border-width: 2px;
    display: none;
    flex-shrink: 0;

    th:hover &,
    &:hover {
      display: flex;
    }

    ${
      $active &&
      css`
        display: flex;
      `
    }
  `,
);

StyledIconButton.defaultProps = { size: 'sm', variant: 'outlined', color: 'primary' };

const StyledStack = styled(
  Stack,
  transientOptions,
)<{ $active: boolean }>(
  ({ $active }) => css`
    opacity: 0;
    transition: opacity 200ms ease;

    th:hover &,
    &:hover {
      opacity: 1;
    }

    ${
      $active &&
      css`
        opacity: 1;
      `
    }
  `,
);

type TableHeaderCellProps<TData extends RowData, TValue> = {
  header: Header<TData, TValue>;
  filterOpen: string | undefined;
  onFilterToggle: (headerId: string) => void;
};

export function TableHeaderCell<TData extends RowData, TValue>({
  header,
  filterOpen,
  onFilterToggle,
}: TableHeaderCellProps<TData, TValue>) {
  const children = (
    <th
      colSpan={header.colSpan}
      style={{
        width: header.column.getSize(),
        minWidth: header.column.getSize(),
        maxWidth: header.column.getSize(),
        borderBottom: `2px solid ${globalTheme.palette.divider}`,
      }}
    >
      {header.isPlaceholder ? null : (
        <Stack direction="row" alignItems="center">
          <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {flexRender(header.column.columnDef.header, header.getContext())}
          </Typography>

          <StyledStack
            $active={filterOpen === header.id || !!header.column.getIsSorted() || header.column.getIsFiltered()}
            direction="row"
            flexWrap="nowrap"
            marginLeft={0.5}
            gap={0.5}
          >
            {header.column.getCanFilter() && (
              <StyledIconButton
                title="Filter"
                $active={filterOpen === header.id || header.column.getIsFiltered()}
                onClick={() => onFilterToggle(header.id)}
              >
                <FilterIcon style={{ width: 12, height: 'auto' }} />
              </StyledIconButton>
            )}
            <StyledIconButton
              title="Sortieren"
              onClick={header.column.getToggleSortingHandler()!}
              $active={!!header.column.getIsSorted()}
            >
              <AngleUpIcon
                style={{
                  width: 12,
                  height: 'auto',
                  ...(header.column.getIsSorted() === 'desc' && { transform: 'rotate(180deg)' }),
                }}
              />
            </StyledIconButton>
          </StyledStack>
        </Stack>
      )}
    </th>
  );

  return header.column.getSize() <= 128 ? (
    <Tooltip
      placement="top-start"
      variant="plain"
      title={flexRender(header.column.columnDef.header, header.getContext())}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
}
